import type { DataTableColumnDef } from 'components/data-table';
import type { TractNRI } from 'components/nri-table';
import { TotalSlotInterestCell } from './total-slot-interest-cell';
import { tagsTextColumn } from 'lib/asset-aliases';
import { linkCellRenderer } from './utils';

/** The four NPV pricing columns' row fields — the only keys #npvColumn takes. */
type NpvKey = Extract<
  keyof TractNRI,
  | 'targetNpvExcluded'
  | 'maxNpvExcluded'
  | 'targetNpvIncluded'
  | 'maxNpvIncluded'
>;

/**
 * Whole-dollar pricing column (Eval 3.0 NPV): currency formatter forced to zero
 * decimals. Sortable server-side (the NPV keys are top-level row fields; blank
 * until a completed calc backs the request).
 *
 * @param {string} accessorKey - The row field.
 * @param {string} header - The column header label.
 * @return {object} The DataTable column definition.
 */
function npvColumn<T extends TractNRI>(
  accessorKey: NpvKey,
  header: string
): DataTableColumnDef<T> {
  return {
    accessorKey,
    header,
    size: 160,
    meta: {
      type: 'currency',
      format: { minDecimalPlaces: 0, maxDecimalPlaces: 0 },
    },
  };
}

/**
 * The Unit link, the four NPV pricing columns and Total Slot NRI / WI, shared by
 * the Slot and Slot Allocation grids, which show them in the same order.
 *
 * @return {object[]} The DataTable column definitions.
 */
export function unitAndInterestColumns<
  T extends TractNRI,
>(): DataTableColumnDef<T>[] {
  return [
    {
      accessorKey: 'unit',
      header: 'Unit',
      size: 140,
      cell: (info) =>
        linkCellRenderer(info.getValue<string>(), info.row.original.unitHref),
    },
    // The unit's aliases as comma-delimited text, read when the table is read; blank with none.
    tagsTextColumn<T>({
      accessorKey: 'unitTags',
      header: 'Unit Tags',
      size: 180,
    }),
    npvColumn<T>('targetNpvExcluded', 'Target - Broker'),
    npvColumn<T>('maxNpvExcluded', 'Max - Broker'),
    npvColumn<T>('targetNpvIncluded', 'Target - SIM'),
    npvColumn<T>('maxNpvIncluded', 'Max - SIM'),
    {
      accessorKey: 'totalSlotNri',
      header: 'Total Slot NRI',
      // 160 to match the SIM pricing columns (Target/Max - SIM) so the header
      // filter's Min/Max boxes match the other numeric columns. The value +
      // source pill + icons wrap to extra lines (meta.wrap) when they don't fit.
      size: 160,
      meta: { type: 'decimal' },
      cell: (info) => (
        <TotalSlotInterestCell
          row={info.row.original}
          value={info.getValue<number | null>()}
          calculationType="NRI"
        />
      ),
    },
    {
      accessorKey: 'totalSlotWi',
      header: 'Total Slot WI',
      // 160 to match the SIM pricing columns — see Total Slot NRI above.
      size: 160,
      meta: { type: 'decimal' },
      cell: (info) => (
        <TotalSlotInterestCell
          row={info.row.original}
          value={info.getValue<number | null>()}
          calculationType="WI"
        />
      ),
    },
  ];
}
